<script>
import { validationMixin } from 'vuelidate'
import { Helper } from '@/mixin/helper'
import { required } from 'vuelidate/lib/validators'
import * as sensor from '@/services/rest-api-tools/lib/sensor/sensor'
import { isInteger } from '@/helpers/validators'
import Confirmation from '@/components/widgets/modals/Confirmation'
import McToggle from '@/components/McToggle/index.vue'

export default {
  components: {
    Confirmation,
    McToggle,
  },
  mixins: [Helper, validationMixin],
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    sensor: {
      type: Object,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    region: {
      type: String,
      default: 'SG'
    },
  },

  data() {
    return {
      sensorCode: 4,
      pinCode: 0,
      sensorCodeList: [
        { id: 4, name: 'Door 1' },
        { id: 5, name: 'Door 2' },
      ],
      reverseDoorList: [
        { id: 'reversed', name: this.$t('sensor.active') },
        { id: null, name: this.$t('sensor.inactive') },
      ],
      modalConfirm: false,
      form: {
        idleHigh: null,
        idleLow: null,
        alertLow: null,
        alertHigh: null,
        macAddress: null,
        alertMinTime: null,
        isDefault: false,
        toggleDefaultAlertMinTime: false,
        statusText: null,
      },
      sensors: [],
    }
  },

  validations: {
    form: {
      alertMinTime: {
        required(val) {
          // Only apply validation when toggle is true
          return this.form.toggleDefaultAlertMinTime ? required(val) : true
        },
        minValue(val) {
          // Only apply validation when toggle is true
          return this.form.toggleDefaultAlertMinTime ? parseInt(val) > parseInt(0) : true
        },
        isInteger(val) {
          // Only apply validation when toggle is true
          return this.form.toggleDefaultAlertMinTime ? isInteger(val) : true
        },
      },
    },
  },

  watch: {
    async sensorCode(newVal) {
      if (newVal) {
        const sensor_type = this.sensor.sensor_type.find((v) => v.sensor_code === newVal)
        if (sensor_type) {
          this.setValue(sensor_type)
          this.getSensors()
        } else {
          this.form.macAddress = null
          this.form.alertMinTime = null
          this.form.statusText = null
          this.sensors = []
        }
        this.form.alertMinTime ? this.form.toggleDefaultAlertMinTime = true : this.form.toggleDefaultAlertMinTime = false
      }
    },
  },

  created() {
    const sensor_type = this.sensor?.sensor_type.find((v) => v.sensor_code === this.sensorCode)
    if (sensor_type) {
      this.pinCode = sensor_type.pin_code
      this.form.macAddress = sensor_type.mac_address
      this.form.alertMinTime = sensor_type.alert_min_time
      this.form.alertMinTime ? this.form.toggleDefaultAlertMinTime = true : this.form.toggleDefaultAlertMinTime = false
      this.form.statusText = sensor_type?.status_text !== 'reversed' ? null : 'reversed'
    }
    this.getSensors()
  },

  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(newVal) {
        this.$emit('update:modal', newVal)
      },
    },
    loading: {
      get() {
        return this.isLoading
      },
      set(newVal) {
        this.$emit('update:isLoading', newVal)
      },
    },
  },

  methods: {
    setValue(source) {
      this.pinCode = source.pin_code
      this.form.macAddress = source.mac_address
      this.form.alertMinTime = source.alert_min_time
      this.form.statusText = source?.status_text !== 'reversed' ? null : 'reversed'
    },

    async getSensors() {
      const queryParameters = {
        includes: 'vehicle:left:vehicleId',
        filters: `vehicle:licensePlate:eq:${this.vehicles[0]?.license_plate};sensorType:sensorCode:eq:${this.sensorCode}`,
        page: 1,
        size: 1,
      }
      const resp = await sensor.get(queryParameters, {
        headers: {
          'X-Region': this.Region,
        },
      })
      if (!resp?.data) return
      this.sensors = resp.data
    },

    async handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.$v.form.$touch()
      if (this.$v.form.$error) {
        return
      }
      this.modalConfirm = true
    },

    afterConfirm() {
      let pinCode = 0
      if (this.sensor.sensor_type) {
        const sensor_type = this.sensor?.sensor_type.find((v) => v.sensor_code === this.sensorCode)
        if (!sensor_type) this.$toast.error(this.$t('error.sensorDoorNotAvailable'))
        pinCode = sensor_type.pin_code
      }
      const payload = {
        sensor_code: this.sensorCode,
        pin_code: pinCode,
        old_pin_code: this.pinCode,
        alert_min_time: this.form.alertMinTime ? parseInt(this.form.alertMinTime) : null,
        status_text: this.form.statusText
      }
      if (pinCode === 0) {
        this.$toast.error(this.$t('error.sensorDoorNotAvailable'))
      } else {
        this.$emit('beforeSubmit', { payload, overwrite: true })
      }
    },

    handleChangeAlertMinTime() {
      this.form.alertMinTime = this.form.toggleDefaultAlertMinTime ? parseInt(300) : null // 5 minutes or 300 seconds
    },
  },
}
</script>
<template>
  <div>
    <b-modal
      v-model="showModal"
      class="mc-modal"
      id="modal-form"
      title="Door Setting"
      size="lg"
      centered
      :hide-header-close="loading"
      no-close-on-backdrop
    >
      <div class="card-body mc-modal__filter p-2">
        <b-form @submit.stop.prevent="handleSubmit">
          <b-row>
            <b-col cols="6">
              <label class="mt-3 required">{{ $t('sensor.type') }}</label>
              <v-select
                v-model="sensorCode"
                :options="sensorCodeList"
                placeholder="Pilih sensor"
                label="name"
                :reduce="(v) => v.id"
              />
            </b-col>
            <b-col cols="6">
              <label class="mt-3">{{ $t('sensor.macAddress') }}</label>
              <b-form-input :value="form.macAddress || '-'" type="text" disabled />
            </b-col>
            <b-col cols="6">
              <McToggle
                class="mt-3 mb-3"
                v-model="form.toggleDefaultAlertMinTime"
                :label="$t('sensor.defaultAlertMinTime')"
                @change="handleChangeAlertMinTime"
                v-b-toggle.accordion-3
              />
              <div v-if="form.toggleDefaultAlertMinTime" class="mt-3 mb-3">
                <label class="required">
                  {{ $t('sensor.alertMinTime') }}
                </label>
                <b-form-input
                  v-model="form.alertMinTime"
                  type="number"
                  :placeholder="$t('sensor.alertMinTime')"
                  :class="{
                    'is-invalid': $v.form.alertMinTime.$error,
                  }"
                />
                <div v-if="$v.form.alertMinTime.$error" class="invalid-feedback">
                  <span v-if="!$v.form.alertMinTime.required">
                    {{ $t('sensor.required') }}
                  </span>
                  <span v-else-if="!$v.form.alertMinTime.minValue">
                    {{ $t('sensor.valueMoreThan') + ' 0' }}
                  </span>
                  <span v-else-if="!$v.form.alertMinTime.isInteger">
                    {{ $t('global.validation.isInteger') }}
                  </span>
                </div>
              </div>
              <div>
                <label >Reversed <i v-b-tooltip.hover.top="$t('sensor.doorTooltip')" class="mc-fa icon-fill_info"></i></label>
                <v-select
                  v-model="form.statusText"
                  :options="reverseDoorList"
                  placeholder="Pilih sensor"
                  label="name"
                  :clearable="false"
                  :reduce="(v) => v.id"
                />
              </div>
            </b-col>
          </b-row>
        </b-form>
      </div>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col cols="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel()" :disabled="loading" class="me-3">
              {{ $t('global.cancel') }}
            </b-button>
            <b-button @click="handleSubmit" variant="primary" :disabled="loading">
              <b-spinner v-if="loading" small type="grow"></b-spinner>
              {{ loading ? 'Loading...' : $t('global.save') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
    <Confirmation
      v-if="modalConfirm"
      :modal.sync="modalConfirm"
      message="Yakin ingin melakukan Pengaturan Parameter"
      @confirmed="afterConfirm"
    />
  </div>
</template>
